/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import types */
import type { DeviceSensorMetadataGroup } from '@/types/deviceSensor';

export type KpiOverviewCustomer = 'muenster' | 'neutraubling';

export interface KpiOverviewMetricConfig {
    active: boolean;
    description: string;
    unit: string | null;
}

export interface KpiOverviewGroupConfig {
    customer: KpiOverviewCustomer;
    title: string;
    group: DeviceSensorMetadataGroup;
    overview: string;
    exampleDevice: string;
    kpis: Readonly<Record<string, KpiOverviewMetricConfig>>;
}

export const KPI_OVERVIEW_GROUPS: readonly KpiOverviewGroupConfig[] = [
    {
        customer: 'neutraubling',
        title: 'PV Prediction',
        group: 'PV',
        overview: 'Overview of all available PV prediction metrics.',
        exampleDevice: 'GN.Z34',
        kpis: {
            pred_new: { active: false, description: 'Predicted PV power output, trained on the early lifespan of the system', unit: 'W' },
            pred_recent: { active: false, description: 'Predicted PV power output, trained on the current system', unit: 'W' },
            ratio_30: { active: false, description: 'Energy ratio for the last 30 days between the measured power output over the predicted power output', unit: null },
            new_30: { active: false, description: 'Energy amount for the last 30 days of the <pred_new> PV prediction', unit: 'Wh' },
            recent_30: { active: false, description: 'Energy amount for the last 30 days of the <pred_recent> PV prediction', unit: 'Wh' },
            ref_30: { active: false, description: 'Energy amount for the last 30 days of the measured PV power', unit: 'Wh' },
            warning: { active: false, description: 'Long term degradation indicator. Compares a new system PV prediction with the current system PV prediction', unit: null },
            error: { active: false, description: 'Failure indicator, for power outages or communication failures', unit: null },
            loss_pct: { active: false, description: 'Do not show, only for debugging', unit: null },
            lost_30: { active: false, description: 'Energy amount for the last 30 days of the <pred_recent> PV prediction while an error was detected', unit: 'Wh' },
            lost_1: { active: false, description: 'Energy amount for the last day of the <pred_recent> PV prediction while an error was detected', unit: 'Wh' },
            DTC: { active: false, description: 'Combined PV status code. Values from 100 indicate a warning; values from 200 indicate an error.', unit: null },
            'loss.7_abs': { active: true, description: 'Minderertrag: 7-Day Absolute Performance', unit: null },
            'loss.30_abs': { active: true, description: 'Minderertrag: 30-Day Absolute Performance', unit: null },
            'loss.180_abs': { active: true, description: 'Minderertrag: 180-Day Absolute Performance', unit: null },
            'loss.360_abs': { active: true, description: 'Minderertrag: 360-Day Absolute Performance', unit: null },
            'loss.7_rel': { active: true, description: 'Minderertrag: 7-Day Relative Performance', unit: null },
            'loss.30_rel': { active: true, description: 'Minderertrag: 30-Day Relative Performance', unit: null },
            'loss.180_rel': { active: true, description: 'Minderertrag: 180-Day Relative Performance', unit: null },
            'loss.360_rel': { active: true, description: 'Minderertrag: 360-Day Relative Performance', unit: null },
            'loss.7_hours': { active: true, description: 'Minderertrag: 7-Day Full-Load Hours Lost', unit: null },
            'loss.30_hours': { active: true, description: 'Minderertrag: 30-Day Full-Load Hours Lost', unit: null },
            'loss.180_hours': { active: true, description: 'Minderertrag: 180-Day Full-Load Hours Lost', unit: null },
            'loss.360_hours': { active: true, description: 'Minderertrag: 360-Day Full-Load Hours Lost', unit: null },
            'loss.dtc': { active: true, description: 'Minderertrag: DTC-Level', unit: null },
            'redispatch.active': { active: true, description: 'Abschaltung wegen Redispatch: Active (0 oder 1)', unit: null },
            'redispatch.dtc': { active: true, description: 'Abschaltung wegen Redispatch: DTC Code', unit: null },
            'redispatch.power_lost': { active: true, description: 'Abschaltung wegen Redispatch: Energieverlust', unit: null },
            'outage.active': { active: true, description: 'Ausfall der Anlage / Daten: Active (0 oder 1)', unit: null },
            'outage.dtc': { active: true, description: 'Ausfall der Anlage / Daten: DTC Code', unit: null },
            'outage.evidance': { active: true, description: 'Ausfall der Anlage / Daten: Aktuell keine Erklärung', unit: null },
            'outage.fault_age': { active: true, description: 'Ausfall der Anlage / Daten: Aktuell keine Erklärung', unit: null },
            'outage.healthy_age': { active: true, description: 'Ausfall der Anlage / Daten: Aktuell keine Erklärung', unit: null },
        },
    },
    {
        customer: 'neutraubling',
        title: 'Base Load',
        group: 'BL',
        overview: 'Overview of all available base load metrics.',
        exampleDevice: 'GN.Z04',
        kpis: {
            CostDay: { active: true, description: 'Preis pro Tag: BL.DeviationDay * 0.30 € (negative Werte auf 0 gesetzt)', unit: '€' },
            CostOverall: { active: true, description: 'Aufsummierung der Preise pro Tag: Gesamtkosten über den gesamten Zeitraum', unit: '€' },
            Deviation: { active: true, description: 'Absolute Abweichung BL.Min zu BL.Threshold', unit: 'kW' },
            DeviationDay: { active: true, description: 'Absolute Abweichung pro Tag (BL.Deviation * 24)', unit: 'kW' },
            DeviationPercent: { active: true, description: 'Prozentuale Abweichung BL.Min zu BL.Threshold', unit: '%' },
            Min: { active: true, description: 'Kleinster Energie-Wert der letzten 24 Stunden', unit: 'kW' },
            MinOverall: { active: true, description: 'Kleinster insgesamt bekannter Energie-Wert über den gesamten Zeitraum', unit: 'kW' },
            Severity: {
                active: true,
                description: 'DTC (Severity Code) der letzten 24 Stunden:\n' +
                    ' - 0: OK (< 5% Abweichung)\n' +
                    ' - 100: Toleriert (< 10% Abweichung)\n' +
                    ' - 201: Tolerierung überschritten (< 10% Abweichung)\n' +
                    ' - 202: Kritisch (> 10% Abweichung)',
                unit: 'int: [0|100|201|202]',
            },
            Threshold: { active: true, description: 'Schwellenwert für Statusermittlung', unit: 'kW' },
            ToleranceCounter: { active: true, description: 'Toleranz-Zähler: Anzahl Abweichungen >= 5%', unit: 'int' },
        },
    },
    {
        customer: 'neutraubling',
        title: 'Heating Energy',
        group: 'HE',
        overview: 'Overview of all available heating energy metrics.',
        exampleDevice: 'GN.Z13',
        kpis: {
            BaseLoad: { active: true, description: 'Heizgrundlast (jeden Tag gleich)', unit: 'kW' },
            CutoffTemperature: { active: true, description: 'Cutoff Temperatur ab der nicht mehr geheizt wird (jeden Tag gleich)', unit: '°C' },
            Energy: { active: true, description: 'Durchschnitt Heizenergiewert der letzten 24 Stunden', unit: 'kWh' },
            EnergyAmountTrain: { active: true, description: 'Gesamt-Heizverbrauch des Trainingszeitraums (jeden Tag gleich)', unit: 'kWh' },
            OutsideTemperature: { active: true, description: 'Durchschnittstemperaturwert der letzten 24 Stunden (Außentemperatur)', unit: '°C' },
            SavedCosts: { active: true, description: 'Kumulierte gesparte Kosten ab Trainingsbeginn: HE.SavedEnergy * 0.30 €', unit: '€' },
            SavedEnergy: { active: true, description: 'Kumulierte gesparte Energie ab Trainingsbeginn', unit: 'kWh' },
            WeekendReductionPercent: { active: true, description: 'Wochenendreduktion in Prozent über den gesamten Zeitraum (jeden Tag gleich)', unit: '%' },
            WeekendReductionValue: { active: true, description: 'Wochenendabsenkung in absolut über den gesamten Zeitraum (jeden Tag gleich)', unit: 'kWh' },
        },
    },
    {
        customer: 'muenster',
        title: 'Water loss',
        group: 'WL',
        overview: 'Overview of all available water-loss metrics.',
        exampleDevice: 'Mue.0005.W_00000939',
        kpis: {
            V_loss: { active: true, description: 'Cumulative water loss in m³', unit: 'm³' },
            qv_loss_7d: { active: true, description: 'Water loss over the last 7 days in m³/h', unit: 'm³/h' },
            dtc_7d: { active: true, description: 'DTC value over the last 7 days', unit: null },
            dtc: { active: true, description: 'DTC value for the last day', unit: null },
        },
    },
];
